/**
 * #676 Work Board History paging.
 *
 * History pages the finished cards from `brunel.work_board_history_index`, one server range at
 * a time, and fetches only the rows that page names. The page feeds the one `buildWorkBoard`
 * projection; nothing here decides a card's column or precedence, keeps a copy of history, or
 * writes anything.
 */
import type {
  CommandPlanRow, FinishedRun, MissionTask, ProjectKey, Reader, RoadmapItemRow, TaskActionRow, WorkBoardHistoryRow,
} from './data';
import { buildWorkBoard, type WorkBoardCard, type WorkBoardInput } from './work-board';

export const WORK_BOARD_PAGE_SIZES = [20, 50, 100] as const;
export type WorkBoardPageSize = (typeof WORK_BOARD_PAGE_SIZES)[number];
export const DEFAULT_WORK_BOARD_PAGE_SIZE: WorkBoardPageSize = 20;

export function workBoardPageSize(value: number): WorkBoardPageSize {
  return (WORK_BOARD_PAGE_SIZES as readonly number[]).includes(value) ? value as WorkBoardPageSize : DEFAULT_WORK_BOARD_PAGE_SIZE;
}

/** The inclusive server range for a 1-based page. */
export function pageRange(page: number, pageSize: WorkBoardPageSize): { readonly from: number; readonly to: number } {
  const safePage = Math.max(1, Math.floor(page));
  return { from: (safePage - 1) * pageSize, to: safePage * pageSize - 1 };
}

export type WorkBoardHistoryReader = Pick<Reader,
  'workBoardHistoryPage' | 'tasksByKeys' | 'finishedRunsByKeys' | 'commandPlansByIds' | 'roadmapItemsByIds' | 'taskActionsByKeys'>;

export interface WorkBoardHistorySlice {
  readonly ok: true;
  readonly page: number;
  readonly pageSize: WorkBoardPageSize;
  /** Identity keys in History order; cards are drawn in exactly this order. */
  readonly order: readonly string[];
  /** Exact total when the Data API returned one; null means only the page is known. */
  readonly total: number | null;
  readonly hasPrevious: boolean;
  readonly hasNext: boolean;
  readonly tasks: readonly MissionTask[];
  readonly results: readonly FinishedRun[];
  readonly commandPlans: readonly CommandPlanRow[];
  readonly roadmap: readonly RoadmapItemRow[];
  readonly taskActions: readonly TaskActionRow[];
}

export type WorkBoardHistoryResult = WorkBoardHistorySlice | { readonly ok: false; readonly message: string };

function ids(rows: readonly WorkBoardHistoryRow[], pick: (row: WorkBoardHistoryRow) => string | null): string[] {
  return [...new Set(rows.map(pick).filter((id): id is string => id !== null))];
}

/** Exact (project, id) pairs, in page order, for the project-scoped tables (tasks, runs). */
function keys(rows: readonly WorkBoardHistoryRow[], pick: (row: WorkBoardHistoryRow) => string | null): ProjectKey[] {
  const seen = new Map<string, ProjectKey>();
  for (const row of rows) {
    const id = pick(row);
    if (id === null || row.project_id === null) continue;
    seen.set(`${row.project_id}:${id}`, { projectId: row.project_id, id });
  }
  return [...seen.values()];
}

/** A hydration read that is skipped (not sent) when the page names nothing of that kind. */
async function byIds<T>(list: readonly unknown[], load: () => Promise<{ ok: true; rows: readonly T[] } | { ok: false; message: string }>): Promise<readonly T[] | null> {
  if (list.length === 0) return [];
  const read = await load();
  return read.ok ? read.rows : null;
}

/** One History page: the identities, then only the rows those identities name. */
export async function loadWorkBoardHistory(
  reader: WorkBoardHistoryReader,
  orgId: string,
  page: number,
  pageSize: WorkBoardPageSize,
): Promise<WorkBoardHistoryResult> {
  const safePage = Math.max(1, Math.floor(page));
  const { from, to } = pageRange(safePage, pageSize);
  const index = await reader.workBoardHistoryPage(orgId, from, to);
  if (!index.ok) return { ok: false, message: index.message };
  const rows = index.rows;
  // A 'finished' identity is a run whose task this reader cannot see, so only 'task' rows name tasks.
  const taskKeys = keys(rows.filter((row) => row.source_kind === 'task'), (row) => row.task_id);
  // The board draws a finished run only when no task row outranks it, so a run is fetched only
  // for a finished identity, never to decorate a task card.
  const runKeys = keys(rows.filter((row) => row.source_kind === 'finished'), (row) => row.assignment_id);
  const planIds = ids(rows, (row) => row.command_plan_id);
  const roadmapIds = ids(rows, (row) => row.roadmap_item_id);
  const [tasks, results, commandPlans, roadmap, taskActions] = await Promise.all([
    byIds(taskKeys, () => reader.tasksByKeys(orgId, taskKeys)),
    byIds(runKeys, () => reader.finishedRunsByKeys(orgId, runKeys)),
    byIds(planIds, () => reader.commandPlansByIds(orgId, planIds)),
    byIds(roadmapIds, () => reader.roadmapItemsByIds(orgId, roadmapIds)),
    byIds(taskKeys, () => reader.taskActionsByKeys(orgId, taskKeys)),
  ]);
  if (tasks === null || results === null || commandPlans === null || roadmap === null || taskActions === null) {
    return { ok: false, message: 'Could not load data.' };
  }
  const total = index.total;
  return {
    ok: true,
    page: safePage,
    pageSize,
    order: rows.map((row) => row.identity_key),
    total,
    hasPrevious: safePage > 1,
    // With a count the answer is exact; without one, a full page may have more after it.
    hasNext: total !== null ? to + 1 < total : rows.length === pageSize,
    tasks, results, commandPlans, roadmap, taskActions,
  };
}

/** The identity a built card stands for, exactly as the view's identity_key writes it. */
export function workBoardCardIdentity(card: WorkBoardCard): string {
  if (card.taskId !== null && card.projectId != null) return `task:${card.projectId}:${card.taskId}`;
  if ((card.sourceKind === 'command_plan' || card.sourceKind === 'roadmap') && card.refId !== null) {
    return `${card.sourceKind}:${card.refId}`;
  }
  return card.id;
}

/** "1–20 of 147" when the total is known, otherwise just "Page 2"; never an invented total. */
export function historyRangeLabel(page: number, pageSize: WorkBoardPageSize, shown: number, total: number | null): string {
  if (total === 0 || (total === null && shown === 0 && page === 1)) return 'No finished work yet';
  if (total === null) return `Page ${page}`;
  if (shown === 0) return `Page ${page} · nothing here (${total} in all)`;
  const first = (page - 1) * pageSize + 1;
  return `${first}–${first + shown - 1} of ${total}`;
}

/** One History page as cards, built by the same projection and drawn in the page's order. */
export function buildHistoryCards(
  reference: Pick<WorkBoardInput, 'orgId' | 'workers' | 'projects' | 'capacity' | 'nowMs'>,
  slice: WorkBoardHistorySlice,
): WorkBoardCard[] {
  const model = buildWorkBoard({
    ...reference,
    plans: [], running: [], workerTerminals: [], approvals: [],
    missionTasks: slice.tasks, results: slice.results, commandPlans: slice.commandPlans, roadmap: slice.roadmap,
  });
  const position = new Map(slice.order.map((key, index) => [key, index]));
  return model.columns.flatMap((column) => column.cards)
    .map((card, index) => ({ card, at: position.get(workBoardCardIdentity(card)) ?? slice.order.length + index }))
    .sort((a, b) => a.at - b.at)
    .map((item) => item.card);
}
